import { DecryptedPatient } from '@icure/cardinal-sdk'
import React, { ReactElement, useEffect, useState } from 'react'
import './index.less'
import '@icure/be-hubs-access/styles.css'

import { CertificateBadge, DocumentTree, HealthcarePartyBadge, PatientConsentBadge, TherapeuticLinkBadge, useBeCardinalContext } from '@icure/be-hubs-access'
import { HubStatus } from '@icure/be-hubs-access/provider/UseBeCardinalContext'

import { Alert, Button, Collapse, Tag } from 'antd'
import { EditOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { CustomModal } from '../../../../common/CustomModal'
import { getPatientDataFormated } from '../../../../../helpers/patientDataManipulations'
import { useTranslation } from 'react-i18next'
import { ModalCreatePatientOnHub, ModalCreatePatientOnHubFormType } from './ModalCreatePatientOnHub'
import { ModalPatientForm } from '../ModalPatientForm'
import { createPortal } from 'react-dom'
import { AccountTabKey, ModalManageAccount } from '../../../../user/ModalManageAccount'
import { SpinLoader } from '../../../../common/SpinLoader'

interface ModalUploadReportProps {
  isVisible: boolean
  onClose: () => void
  patient: DecryptedPatient
  healthcarePartyId?: string
  practitionerSsin?: string
  pdfReportBlob: Blob
  selectedHub: string
}

export const ModalUploadReportToHub = ({ isVisible, onClose, patient, healthcarePartyId, practitionerSsin, pdfReportBlob, selectedHub }: ModalUploadReportProps): ReactElement => {
  const handleOnClose = () => {
    onClose()
  }

  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const patientSsin = patient.ssin ?? patient.identifier?.find((id) => id.type?.type === 'SSIN')?.value
  const [hubStatus, setHubStatus] = useState<HubStatus | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [createPatientOnHubModalOpen, setCreatePatientOnHubModalOpen] = useState<boolean>(false)
  const [editPatientModalOpen, setEditPatientModalOpen] = useState<boolean>(false)
  const [manageAccountModalOpen, setManageAccountModalOpen] = useState<boolean>(false)

  const { isCertificateValid, loadPatientFromHub, createPatientInHub, uploadDocumentToHub, hcp: practitionerFromTheHub } = useBeCardinalContext()
  const refresh = async () => {
    if (!patientSsin) return
    setIsLoading(true)
    try {
      const h = await loadPatientFromHub(patientSsin.replace(/[^0-9]/g, ''))
      setHubStatus(h)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refresh().catch((e) => {
      console.error('Error loading patient hub status:', e)
    })
  }, [patientSsin, isCertificateValid])

  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)

  const { userNameOneString } = getPatientDataFormated(patient, t, currentLanguage)

  const handleCreatePatient = (values: ModalCreatePatientOnHubFormType) => {
    createPatientInHub({
      ssin: values.ssin.replace(/[^0-9]/g, ''),
      idCardNumber: values.idCardNumber,
      firstName: values.firstName,
      lastName: values.lastName,
    })
      .then(() => {
        setCreatePatientOnHubModalOpen(false)
        refresh().catch((e) => {
          console.error('Error loading patient hub status after creation:', e)
        })
      })
      .catch((err: unknown) => {
        console.error('Error creating patient in hub:', err)
      })
  }

  const handleOnUpload = () => {
    setUploading(true)
    setUploadError(null)
    setUploadSuccess(null)
    try {
      uploadDocumentToHub(
        { ...patient, ssin: patientSsin?.replace(/[^0-9]/g, '') },
        new File([pdfReportBlob], `Medical_Report_${(userNameOneString ?? '').concat(`_${patientSsin ?? ''}`).replace(' ', '_')}.pdf`, { type: 'application/pdf' }),
        'diarynote',
      )
        .then(() => {
          setUploadSuccess(
            t('modalUploadReportToHub.upload.success', {
              ssin: patientSsin,
            }),
          )
        })
        .catch((err: unknown) => {
          console.error('Error uploading document to hub:', err)
          const message = err instanceof Error ? err.message : typeof err === 'string' ? err : t('modalUploadReportToHub.upload.error')
          setUploadError(message)
        })
        .finally(() => {
          setUploading(false)
        })
    } catch (err: unknown) {
      console.error('Error uploading document to hub:', err)
      const message = err instanceof Error ? err.message : typeof err === 'string' ? err : t('modalUploadReportToHub.upload.error')
      setUploadError(message)
    }
  }

  const hasNoTherapeuticLinks = () => hubStatus && !isLoading && hubStatus?.hubPatientTherapeuticLinks?.length === 0 && hubStatus?.fedPatientTherapeuticLinks?.length === 0
  const hasNoPatientOnHub = () => hubStatus && !isLoading && !hubStatus?.patientDetails
  const hubPatientConsentGiven = () => hubStatus && !isLoading && !!hubStatus?.hubPatientConsent && !!hubStatus?.fedPatientConsent
  const practitionerHasAccessToTheHub = () => !!practitionerFromTheHub?.hubConsent?.hcparty

  const hubConnectionStatus = [
    {
      key: 'hub',
      title: t('modalUploadReportToHub.status.hub'),
      children: <h5>{selectedHub}</h5>,
    },
    {
      key: 'certificate',
      title: t('modalUploadReportToHub.status.certificate'),
      children: <CertificateBadge />,
    },
    {
      key: 'practitioner',
      title: t('modalUploadReportToHub.status.practitioner', { ssin: practitionerSsin }),
      children: <HealthcarePartyBadge />,
    },
    {
      key: 'patientStatus',
      title: t('modalUploadReportToHub.status.patientStatus', { ssin: patientSsin || 'N/A' }),
      children: hubStatus ? (
        <>
          <PatientConsentBadge
            patientSsin={(patientSsin ?? '').replace(/[^0-9]/g, '')}
            hubConsent={hubStatus.hubPatientConsent}
            fedConsent={hubStatus.fedPatientConsent}
            patientLastName={hubStatus.patientDetails?.lastName}
            patientFirstName={hubStatus.patientDetails?.firstName}
            onConsentUpdated={refresh}
          />
          {hasNoPatientOnHub() && (
            <Tag style={{ cursor: 'pointer' }} onClick={() => setCreatePatientOnHubModalOpen(true)} icon={<ExclamationCircleOutlined />} color="red">
              {t('modalUploadReportToHub.status.noHubRecordTag')}
            </Tag>
          )}
        </>
      ) : isLoading ? (
        <p>{t('modalUploadReportToHub.status.loading')}</p>
      ) : (
        <p>{t('modalUploadReportToHub.status.noStatus')}</p>
      ),
      hidden: !practitionerHasAccessToTheHub(),
    },
    {
      key: 'therapeuticLink',
      title: t('modalUploadReportToHub.status.therapeuticLink'),
      children:
        hubStatus && patientSsin && hubPatientConsentGiven() ? (
          <TherapeuticLinkBadge
            patientSsin={patientSsin.replace(/[^0-9]/g, '')}
            hubTherapeuticLinks={hubStatus.hubPatientTherapeuticLinks}
            fedTherapeuticLinks={hubStatus.fedPatientTherapeuticLinks}
            onLinkUpdated={refresh}
          />
        ) : (
          <p>{t('modalUploadReportToHub.status.noTherapeuticLink')}</p>
        ),
      hidden: !practitionerHasAccessToTheHub(),
    },
  ]

  const getCustomFooter = () => {
    return [
      <div className="modalUploadReportToHub__customFooter" key="customFooter">
        <Button key="changeHub" onClick={() => setManageAccountModalOpen(true)}>
          {t('modalUploadReportToHub.buttons.changeHub')}
        </Button>
        <div className="modalUploadReportToHub__customFooter__right">
          <Button key="cancel" onClick={handleOnClose}>
            {t('modalUploadReportToHub.buttons.cancel')}
          </Button>
          <Button
            key="submit"
            onClick={handleOnUpload}
            disabled={!pdfReportBlob || !patientSsin || uploading || !isCertificateValid || hasNoPatientOnHub() || !hubPatientConsentGiven()}
            type="primary"
          >
            {t('modalUploadReportToHub.buttons.uploadReport')}
          </Button>
        </div>
      </div>,
    ]
  }

  return (
    <>
      <CustomModal title={t('consultations.uploadToHub.button')} isVisible={isVisible} handleClose={handleOnClose} customFooter={getCustomFooter()}>
        <>
          {isLoading && <SpinLoader />}
          <div className="modalUploadReportToHub">
            {!patientSsin ? (
              <Alert
                type="error"
                className="modalUploadReportToHub__alert"
                message={t('modalUploadReportToHub.alerts.patientSsinMissing.title')}
                description={
                  <div className="modalUploadReportToHub__alert__description">
                    <div className="modalUploadReportToHub__alert__description__content">
                      <p>{t('modalUploadReportToHub.alerts.patientSsinMissing.description')}</p>
                    </div>
                    <Button icon={<EditOutlined />} size="small" onClick={() => setEditPatientModalOpen(true)}>
                      {t('modalUploadReportToHub.buttons.editPatient')}
                    </Button>
                  </div>
                }
                showIcon
              />
            ) : !practitionerSsin ? (
              <Alert
                type="error"
                className="modalUploadReportToHub__alert"
                message={t('modalUploadReportToHub.alerts.practitionerSsinMissing.title')}
                description={
                  <div className="modalUploadReportToHub__alert__description">
                    <div className="modalUploadReportToHub__alert__description__content">
                      <p>{t('modalUploadReportToHub.alerts.practitionerSsinMissing.description')}</p>
                    </div>
                    <Button icon={<EditOutlined />} size="small" onClick={() => setManageAccountModalOpen(true)}>
                      {t('modalUploadReportToHub.buttons.editProfile')}
                    </Button>
                  </div>
                }
                showIcon
              />
            ) : (
              <>
                <div className="modalUploadReportToHub__connectionStatus">
                  {hubConnectionStatus
                    .filter((el) => !el.hidden)
                    .map((el) => (
                      <div className="modalUploadReportToHub__connectionStatus__item" key={el.key}>
                        <div className="modalUploadReportToHub__connectionStatus__item__heading">
                          <p>{el.title}</p>
                        </div>
                        <div className="modalUploadReportToHub__connectionStatus__item__content">{el.children}</div>
                      </div>
                    ))}
                </div>

                {hasNoPatientOnHub() && practitionerHasAccessToTheHub() && (
                  <Alert
                    type="error"
                    className="modalUploadReportToHub__alert"
                    message={<h5>{t('modalUploadReportToHub.alerts.missingPatientRecord.title', { hub: selectedHub })}</h5>}
                    description={
                      <div className="modalUploadReportToHub__alert__description">
                        <div className="modalUploadReportToHub__alert__description__content">
                          <p>
                            {t('modalUploadReportToHub.alerts.missingPatientRecord.description', {
                              hub: selectedHub,
                              ssin: patientSsin,
                            })}
                          </p>
                          <p>{t('modalUploadReportToHub.alerts.missingPatientRecord.description2')}</p>
                        </div>

                        <div className="modalUploadReportToHub__alert__description__buttons">
                          <Button icon={<PlusOutlined />} size="small" onClick={() => setCreatePatientOnHubModalOpen(true)}>
                            {t('modalUploadReportToHub.buttons.createPatientRecord')}
                          </Button>

                          <Button icon={<EditOutlined />} size="small" onClick={() => setManageAccountModalOpen(true)}>
                            {t('modalUploadReportToHub.buttons.changeHub')}
                          </Button>
                        </div>
                      </div>
                    }
                  />
                )}
                {!practitionerHasAccessToTheHub() && isCertificateValid && (
                  <Alert
                    showIcon
                    type="error"
                    className="modalUploadReportToHub__alert"
                    description={t('modalUploadReportToHub.alerts.practitionerNoAccess.description', { hub: selectedHub })}
                  />
                )}
                {!hubPatientConsentGiven() && isCertificateValid && (
                  <Alert
                    type="error"
                    className="modalUploadReportToHub__alert"
                    message={t('modalUploadReportToHub.alerts.patientConsentMissing.title')}
                    description={
                      <div className="modalUploadReportToHub__alert__description">
                        <div className="modalUploadReportToHub__alert__description__content">
                          <p>{t('modalUploadReportToHub.alerts.patientConsentMissing.description')}</p>
                        </div>

                        <PatientConsentBadge
                          patientSsin={(patientSsin ?? '').replace(/[^0-9]/g, '')}
                          hubConsent={hubStatus?.hubPatientConsent}
                          fedConsent={hubStatus?.fedPatientConsent}
                          patientLastName={hubStatus?.patientDetails?.lastName}
                          patientFirstName={hubStatus?.patientDetails?.firstName}
                          onConsentUpdated={refresh}
                        />
                      </div>
                    }
                    showIcon
                  />
                )}
                {hubPatientConsentGiven() && hasNoTherapeuticLinks() && (
                  <Alert
                    showIcon
                    type="error"
                    className="modalUploadReportToHub__alert"
                    description={t('modalUploadReportToHub.alerts.noTherapeuticLinks.description', { hub: selectedHub })}
                  />
                )}
                {hubStatus?.patientDetails && !hasNoTherapeuticLinks() && hubPatientConsentGiven() && (
                  <div className="hubDetails">
                    <div className="uploadControls">
                      {(uploadSuccess || uploadError) && (
                        <div className="uploadStatus">
                          {uploadSuccess && <div className="success">{uploadSuccess}</div>}
                          {uploadError && <div className="error">{uploadError}</div>}
                        </div>
                      )}
                    </div>
                    <Collapse
                      defaultActiveKey={'1'}
                      items={[
                        {
                          styles: {
                            body: {
                              background: 'white',
                              borderRadius: '0 0 8px 8px',
                            },
                          },
                          key: '1',
                          label: (
                            <div className="documentsTitle">
                              <p> {t('modalUploadReportToHub.collapse.documentsLabel')}</p>
                            </div>
                          ),
                          children: (
                            <div className="documentsSection">
                              {patientSsin ? <DocumentTree patientSsin={patientSsin.replace(/[^0-9]/g, '')} /> : t('modalUploadReportToHub.collapse.noPatientSsin')}
                            </div>
                          ),
                        },
                      ]}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      </CustomModal>

      {editPatientModalOpen &&
        patient &&
        createPortal(<ModalPatientForm mode="edit" patientToEdit={patient} isVisible={editPatientModalOpen} onClose={() => setEditPatientModalOpen(false)} />, document.body)}

      {manageAccountModalOpen &&
        createPortal(
          <ModalManageAccount
            isVisible={manageAccountModalOpen}
            onClose={() => setManageAccountModalOpen(false)}
            healthcarePartyId={healthcarePartyId}
            defaultActiveTab={AccountTabKey.Hubs}
          />,
          document.body,
        )}

      {createPatientOnHubModalOpen &&
        createPortal(
          <ModalCreatePatientOnHub
            t={t}
            isVisible={createPatientOnHubModalOpen}
            onClose={() => setCreatePatientOnHubModalOpen(false)}
            patient={patient}
            selectedHub={selectedHub}
            onSubmit={(values: ModalCreatePatientOnHubFormType) => handleCreatePatient(values)}
          />,
          document.body,
        )}
    </>
  )
}
